/**
 * Telemetry helpers for the Grit Mile landing page.
 *
 * fireImpression — POST /telemetry/impression once per browser session.
 *   Uses gritmile_sid session cookie to deduplicate within a session.
 *
 * fireConversion — POST /telemetry/conversion after successful form submission.
 *
 * PII rules (per spec):
 *   - source = referrer hostname only (new URL(document.referrer).hostname), "direct" if absent
 *   - userAgent = first 128 chars of navigator.userAgent
 *   - No IP capture, no URL query params
 */

import { SESSION_COOKIE, getCookieValue, getOrCreateSessionCookie } from "./cookie.js";
import { env } from "./env.js";

// ── Source helper ─────────────────────────────────────────────────────────────

function getReferrerSource(): string {
  try {
    if (!document.referrer) return "direct";
    return new URL(document.referrer).hostname;
  } catch {
    return "direct";
  }
}

// ── Impression ────────────────────────────────────────────────────────────────

/**
 * Fire an impression event once per browser session.
 * If gritmile_sid already exists (impression already fired this session),
 * this is a no-op.
 */
export async function fireImpression(variantId: number, source?: string): Promise<void> {
  // Check if impression already fired this session
  const existingSession = getCookieValue(SESSION_COOKIE);
  if (existingSession !== null) return;

  // Create session cookie and fire impression
  const sessionId = getOrCreateSessionCookie();
  const effectiveSource = source ?? getReferrerSource();

  try {
    await fetch(`${env.apiUrl}/telemetry/impression`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variantId, source: effectiveSource, sessionId }),
    });
  } catch {
    // Non-fatal: telemetry failure must not break the page
  }
}

// ── Conversion ────────────────────────────────────────────────────────────────

/**
 * Fire a conversion event after the user successfully signs up.
 */
export async function fireConversion(variantId: number, email: string): Promise<void> {
  try {
    await fetch(`${env.apiUrl}/telemetry/conversion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variantId, email }),
    });
  } catch {
    // Non-fatal
  }
}
